import { useSEO } from "../lib/useSEO";

const SAFETY_TOPICS = [
  {
    label: "Barriers exist for a reason",
    title: "Stay behind railings",
    body: "Railings and barriers are placed exactly where the ground is most unstable, the drop is most dangerous, or the rocks are most slippery. Don't lean over them. Don't step around them for a photo. No selfie is worth your life — and selfie deaths are tragically common at waterfalls worldwide.",
    image: "https://images.unsplash.com/photo-1502082553048-f009c37129b9?w=1200&q=70",
  },
  {
    label: "Most injuries start here",
    title: "Watch for slippery rocks",
    body: "Wet rocks, moss-covered stones and loose gravel cause the majority of waterfall injuries — broken ankles, head impacts, fractured wrists. Wear sturdy closed-toe shoes with grippy soles. Stay on marked tracks. If a surface looks slick, assume it is, and back away.",
    image: "https://images.unsplash.com/photo-1610044847457-f6aabcbb67d3?w=1200&q=70",
  },
  {
    label: "Hidden killers",
    title: "Never swim under a waterfall",
    body: "The plunge pool beneath a fall hides falling debris, submerged ledges and powerful undertows. Strong swimmers are pulled under and held there every year. If you're going to swim, enter slowly and feet-first away from the cascade — never jump or dive.",
    image: "https://images.unsplash.com/photo-1623689259504-33a4179e78e9?w=1200&q=70",
  },
  {
    label: "What you can't see",
    title: "Beware hidden hazards",
    body: "Submerged rocks, fallen branches and debris are invisible from the surface. Water temperatures can drop suddenly and trigger cold-water shock — even on warm days. During droughts, low flow can hide stagnant pools that look swimmable but aren't.",
    image: "https://images.unsplash.com/photo-1568323003715-8c0254336dfa?w=1200&q=70",
  },
  {
    label: "Conditions change fast",
    title: "Mind the weather",
    body: "After heavy rain, currents become powerful within minutes. Crossings that were safe in the morning can become impassable by afternoon. Check the forecast and park alerts before you leave home. If conditions look marginal when you arrive, turn back — the waterfall will still be there next weekend.",
    image: "https://images.unsplash.com/photo-1455218873509-8097305ee378?w=1200&q=70",
  },
  {
    label: "Never go solo",
    title: "Bring a buddy",
    body: "Never swim alone, even at popular waterfalls. Many sites are remote, with patchy phone reception and emergency response times measured in hours. Tell someone your plan, your route, and when you'll be back. Avoid alcohol — it impairs judgement and dramatically increases the risk of accidents in water.",
    image: "https://images.unsplash.com/photo-1551632811-561732d1e306?w=1200&q=70",
  },
  {
    label: "Be self-sufficient",
    title: "Pack like it matters",
    body: "Bring enough drinking water — waterfall water isn't always safe to drink. Sun protection, insect repellent, a small first-aid kit, a charged phone with offline maps, and a whistle for genuinely remote sites. Light-coloured clothing makes leeches, ticks and march flies easier to spot.",
    image: "https://images.unsplash.com/photo-1495312040802-a929cd14a6ab?w=1200&q=70",
  },
];

export default function Safety() {
  useSEO({
    title: "Waterfall safety guide: tips that could save your life | WaterfallFinder",
    description:
      "Essential waterfall safety: slippery rocks, hidden currents, flash floods, cold water shock, animal hazards and more. Read before you go.",
  });

  return (
    <main className="pt-16">
      {/* Intro */}
      <header className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 pt-14 pb-10">
        <p className="text-sm font-semibold uppercase tracking-wider text-amber-700">Safety first</p>
        <h1 className="mt-3 text-5xl md:text-7xl font-extrabold tracking-tight text-stone-900 leading-[0.95] max-w-3xl">
          Stay safe.<br />Come home with the photo.
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-stone-700 leading-relaxed">
          Waterfalls catch people out. Conditions change fast, surfaces are deceptive, and help can be hours away.
          Read these before you head out — they really do save lives.
        </p>
      </header>

      {/* Alternating topic rows */}
      <section className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 pb-10" data-testid="safety-topics">
        {SAFETY_TOPICS.map((t, i) => (
          <TopicRow key={t.title} topic={t} reverse={i % 2 === 1} idx={i} />
        ))}
      </section>

      {/* Footer note */}
      <section className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 mt-20 pb-16 border-t border-stone-200 pt-10">
        <p className="text-sm text-stone-500 max-w-2xl leading-relaxed">
          Before every visit, check your local government or national park website for current alerts, closures and conditions.
          Always make your own assessment of risk on the day. If in doubt, don&apos;t proceed.
        </p>
      </section>
    </main>
  );
}

function TopicRow({ topic, reverse, idx }) {
  return (
    <div
      data-testid={`safety-row-${idx}`}
      className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center py-16 ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}
    >
      {/* Image card */}
      <div className="relative rounded-3xl overflow-hidden aspect-[3/4] bg-stone-100">
        <img
          src={topic.image}
          alt={topic.title}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent" />
        <div className="absolute inset-x-0 top-0 p-8">
          <p className="text-white/85 text-sm font-medium">{topic.label}</p>
          <div className="mt-2 h-px bg-white/40 max-w-[60%]" />
        </div>
        <div className="absolute inset-x-0 bottom-0 p-8">
          <h3 className="text-white text-4xl sm:text-5xl font-extrabold tracking-tight drop-shadow-md">
            {topic.title}
          </h3>
        </div>
      </div>

      {/* Text column */}
      <div className="lg:px-6">
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-stone-900 leading-[0.95]">
          {topic.title}
        </h2>
        <p className="mt-8 text-stone-700 text-lg leading-[1.8]">
          {topic.body}
        </p>
      </div>
    </div>
  );
}
